import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "../../../utils/toast";
import { EmptyState, Loading } from "../../ui";
import { errorMessageOf, newIdempotencyKey, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { destinationOf, placeOf, formatDuration, kindOf, offeredToStudents, TYPE_LABELS } from "../model";
import StudentCallView from "../../calls/StudentCallView";
import EligibilityNote, { PROFILE_PATH } from "./EligibilityNote";
import { STUDENT_KEY, useStudentScholarship } from "./model";

// Who may apply to one programme, and whether the student's academic profile does
function WhoMayApply({ programme, profileRequired }) {
  return (
    <div className="flex flex-col gap-2 text-sm text-slate-700">
      <p>
        <span className="font-medium">Who may apply:</span> {programme.eligibility.length ? programme.eligibility.map((r) => r.label).join("; ") : "any student"}
      </p>
      {!profileRequired && <EligibilityNote eligibility={programme.myEligibility} compact />}
    </div>
  );
}

const applicationPath = (id) => `/my-applications/${id}`;

// One scholarship for a student: what it offers, what to upload, and where to start applying
export default function StudentScholarshipPage() {
  const { scholarshipId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: scholarship, isPending, isError, error, refetch } = useStudentScholarship(scholarshipId);
  const [startKey] = useState(newIdempotencyKey);

  if (isPending) return <Loading label="Loading…" />;
  if (isError)
    return problemOf(error)?.status === 404 ? (
      <EmptyState title="This scholarship is not available" description="The link may be wrong, or it is no longer published." />
    ) : (
      <EmptyState title="Could not load it" onRefresh={refetch} />
    );

  const mine = scholarship.myApplication;
  // A call students are not offered opens only for those who already applied, so they can follow it
  if (!mine && !offeredToStudents(scholarship.type))
    return <EmptyState title="This scholarship is not available" description="The link may be wrong, or it is no longer published." />;
  // A profile is needed to apply; a student whose profile misses a programme's rules may still apply, flagged to HEC
  const profileRequired = Boolean(scholarship.myEligibility?.profileRequired);

  const start = async (programmeId) => {
    try {
      const { data } = await scholarshipsApi.post("student/applications", { scholarshipId, programmeId }, { idempotencyKey: startKey });
      queryClient.invalidateQueries({ queryKey: STUDENT_KEY });
      navigate(applicationPath(data.id));
    } catch (err) {
      const problem = problemOf(err);
      if (problem?.code === "already-applied") return navigate(applicationPath(problem.body.applicationId));
      // The profile was removed after this page loaded
      if (problem?.code === "profile-required") {
        queryClient.invalidateQueries({ queryKey: STUDENT_KEY });
        toast.error(errorMessageOf(err, "Fill in your academic profile before applying."));
        return navigate(PROFILE_PATH);
      }
      console.error(`Failed to start an application to scholarship ${scholarshipId}:`, err);
      toast.error(errorMessageOf(err, "Could not start your application. Please try again."));
    }
  };

  return (
    <StudentCallView
      title={scholarship.name}
      subtitle={`${destinationOf(scholarship)} · ${scholarship.sponsor?.name ?? "HEC"} · ${TYPE_LABELS[scholarship.type] ?? scholarship.type}`}
      aboutTitle={`About this ${kindOf(scholarship.type).one}`}
      description={scholarship.description}
      programmes={scholarship.programmes.map((p) => ({
        id: p.id,
        name: p.name,
        meta: [placeOf(p), p.level?.name, formatDuration(p.durationMonths)].filter(Boolean).join(" · "),
        requirements: p.requirements,
        whoMayApply: <WhoMayApply programme={p} profileRequired={profileRequired} />,
      }))}
      closesAt={scholarship.closesAt}
      open={scholarship.acceptingApplications}
      mine={mine}
      applicationPath={applicationPath}
      canStart={scholarship.acceptingApplications && !mine && !profileRequired}
      onStart={start}
      beforeStart={profileRequired && <EligibilityNote eligibility={scholarship.myEligibility} />}
    />
  );
}
